import { isUsingNewComponents } from "../../add-element/utils";
import { useCurrentSite, useSites } from "../../contexts/models/SiteContext";
import { openModal } from "../../modals/actions";
import { openMediaSelectionModal } from "../../photos/utils";
import InnerNodes from "../../website/components/InnerNodes";
import EditableImageAsset from "./EditableImageAsset";
import { Global } from "@emotion/react";
import elementResizeDetectorMaker from "element-resize-detector";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import TopBarProgress from "react-topbar-progress-indicator";

let erd;

if (typeof window !== "undefined") {
  erd = elementResizeDetectorMaker({
    strategy: "scroll",
  });
}

const selectors = {
  images: "img",
};

export const assetSpecs = {
  l1: {
    themeImage: {
      algorithm: "crop",
      height: 1300,
      key: "theme_image",
      width: 2400,
    },
  },
  l2: {
    brideImage: {
      algorithm: "crop",
      height: 1000,
      key: "bride_image",
      width: 1000,
    },
    groomImage: {
      algorithm: "crop",
      height: 1000,
      key: "groom_image",
      width: 1000,
    },
  },
  l3: {
    themeImage: {
      algorithm: "crop",
      height: 2134,
      key: "theme_image",
      width: 3200,
    },
  },
} as const;

export type AssetSpec = {
  algorithm: "crop";
  height: number;
  key: string;
  width: number;
};

type AssetPathKey = "bride_image" | "groom_image" | "theme_image";

type Position = {
  left: number;
  top: number;
};

type LayoutProps = {
  onEdit: (assetSpec: AssetSpec) => void;
  positions: Position[];
};

const Layout1 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l1.themeImage}
      onEdit={onEdit}
      position={positions[0]}
    />
  </div>
);

const Layout2 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l2.brideImage}
      onEdit={onEdit}
      position={positions[0]}
    />
    <EditableImageAsset
      assetSpec={assetSpecs.l2.groomImage}
      onEdit={onEdit}
      position={positions[1]}
    />
  </div>
);

const assetPaths = {
  bride_image: {
    "div:nth-of-type(3) div:first-of-type div:first-of-type div:first-of-type div:first-of-type img":
      {
        opacity: 0.5,
      },
  },
  groom_image: {
    "div:nth-of-type(3) div:first-of-type div:first-of-type div:first-of-type div:nth-of-type(2) img":
      {
        opacity: 0.5,
      },
  },
  theme_image: {
    img: {
      opacity: 0.5,
    },
  },
};

const Layout3 = ({ onEdit, positions }: LayoutProps) => (
  <div>
    <EditableImageAsset
      assetSpec={assetSpecs.l3.themeImage}
      onEdit={onEdit}
      position={positions[0]}
    />
  </div>
);

export type EditableHeaderProps = {
  children: ReactNode;
};

const EditableHeader = ({ children }: EditableHeaderProps) => {
  const site = useCurrentSite();
  const { setSiteAsset } = useSites();
  const layout = site.themeIdentifier;
  const dispatch = useDispatch();
  const openModalFn = useCallback(
    (name: string, modalProps: Record<string, unknown>) =>
      dispatch(openModal(name, modalProps)),
    [dispatch],
  );
  const [assetTransitioning, setAssetTransitioning] = useState<string | null>(
    null,
  );
  const [positions, setPositions] = useState<{ top: number; left: number }[]>(
    [],
  );
  const [nodes, setNodes] = useState<Element[]>([]);
  const prevLayout = useRef(layout);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const setAsset = (assetSpec: AssetSpec, selectedImage: string) => {
    setAssetTransitioning(assetSpec.key);
    setSiteAsset(assetSpec.key, selectedImage).finally(() => {
      setAssetTransitioning(null);
    });
  };

  const onEdit = (assetSpec: AssetSpec) => {
    if (isUsingNewComponents(site)) {
      openModalFn("HEADER_PHOTO_MANAGER_MODAL", {
        cropAspectRatio: assetSpec.width / assetSpec.height,
        minSize: {
          height: assetSpec.height,
          width: assetSpec.width,
        },
        onSave: (selectedImage) => setAsset(assetSpec, selectedImage),
      });
    } else {
      openMediaSelectionModal(
        openModalFn,
        (selectedImage) => setAsset(assetSpec, selectedImage),
        assetSpec.width / assetSpec.height,
        {
          height: assetSpec.height,
          width: assetSpec.width,
        },
      );
    }
  };

  const updatePositions = useCallback(() => {
    const wrapperNode: Element | null = wrapperRef.current;
    if (!wrapperNode) {
      return;
    }

    const wrapperRect = wrapperNode.getBoundingClientRect();

    const scrollTop = window.pageYOffset;
    const scrollLeft = window.pageXOffset;

    const wrapperBox = {
      left: wrapperRect.left + scrollLeft,
      top: wrapperRect.top + scrollTop,
    };

    const newPositions = nodes.map((node: Element) => {
      const rect = node.getBoundingClientRect();
      const box = {
        left: rect.left + scrollLeft,
        top: rect.top + scrollTop,
      };

      return {
        left: box.left - wrapperBox.left + rect.width,
        top: box.top - wrapperBox.top,
      };
    });

    setPositions(newPositions);
  }, [nodes]);

  useEffect(() => {
    if (nodes.length > 0) {
      updatePositions();
    }

    // Update positions when the container changes size
    const wrapperNode = wrapperRef.current;

    if (erd !== undefined) {
      erd.listenTo(wrapperNode, () => updatePositions());
    }

    return () => {
      if (erd !== undefined) {
        erd.uninstall(wrapperNode);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  useEffect(() => {
    if (prevLayout.current !== layout) {
      setNodes([]);
      setPositions([]);
    }
    prevLayout.current = layout;
  }, [layout, setNodes, setPositions]);

  const onNodesChanged = useCallback(
    (result) => {
      if (result === null) {
        setNodes([]);
      } else {
        setNodes([...result["images"].values()]);
      }
    },
    [setNodes],
  );

  let controls: React.ReactElement | null = null;

  if (positions.length > 0) {
    switch (layout) {
      case "l1":
        if (site.isWedding) {
          controls = <Layout1 onEdit={onEdit} positions={positions} />;
        }
        break;
      case "l2":
        controls = <Layout2 onEdit={onEdit} positions={positions} />;
        break;
      case "l3":
        controls = <Layout3 onEdit={onEdit} positions={positions} />;
        break;
      case "l4":
        break; // no controls necessary
    }
  }

  const assetStyle =
    assetTransitioning === null || assetTransitioning in assetPaths
      ? assetPaths[assetTransitioning as AssetPathKey]
      : {};

  return (
    <div className="customize-header" ref={wrapperRef}>
      <Global
        styles={{
          ".customize-header": assetStyle,
        }}
      />
      {assetTransitioning ? <TopBarProgress /> : null}
      <InnerNodes callback={onNodesChanged} selectAll selectors={selectors}>
        {children}
      </InnerNodes>
      {controls}
    </div>
  );
};

export default EditableHeader;
